import RotateLeftIcon from '@mui/icons-material/RotateLeft'
import RotateRightIcon from '@mui/icons-material/RotateRight'
import {Stack, ToggleButton, ToggleButtonGroup, Typography} from '@mui/material'
import {useSnackbar} from 'notistack'
import {Dispatch, SetStateAction} from 'react'
import {useTranslation} from 'react-i18next'

import {getConnectedSignals} from '../../../hooks'
import {useKilnConfig} from '../../../providers'
import {KilnConfigActionType} from '../../../types'

interface KilnRotationToggleButtonProps {
  setSignalsDisconnectWarning: Dispatch<
    SetStateAction<{
      open: boolean
      signals: string[]
      action: (() => void) | null
    }>
  >
}

export const KilnRotationToggleButton: React.FC<KilnRotationToggleButtonProps> = ({
  setSignalsDisconnectWarning
}) => {
  const {t} = useTranslation()
  const {enqueueSnackbar} = useSnackbar()

  const {kilnLayout, dispatch} = useKilnConfig()

  const handleRotationChange = (e: React.MouseEvent<HTMLElement>, newRotation: boolean | null) => {
    if (newRotation === null) return

    const dispatchActions = () => {
      dispatch({type: KilnConfigActionType.CHANGE_ROTATION, payload: newRotation})
      if (kilnLayout.drive.position === 'top') {
        dispatch({type: KilnConfigActionType.SET_BOTTOM_DRIVE})
      }
      if (kilnLayout.drive.position === 'bottom') {
        dispatch({type: KilnConfigActionType.SET_TOP_DRIVE})
      }
    }

    const connectedSignals = getConnectedSignals(kilnLayout.selectedSignals, kilnLayout.drive.id)

    if (connectedSignals.length > 0 && kilnLayout.drive.position !== 'double') {
      setSignalsDisconnectWarning({
        open: true,
        signals: connectedSignals,
        action: () => {
          dispatchActions()
          enqueueSnackbar(
            `Drive layout changed. Following signals were disconnected: ${connectedSignals.join(
              ', '
            )}`,
            {
              variant: 'warning'
            }
          )
        }
      })
    } else {
      dispatchActions()
    }
  }

  return (
    <Stack spacing={0.5}>
      <Typography variant="caption">{t('kilnConfig.kilnRotationCaption')}</Typography>
      <ToggleButtonGroup
        value={kilnLayout.isRotationClockwise}
        exclusive
        onChange={handleRotationChange}
        color="primary"
        sx={{
          height: '48px',
          background: '#F0F2F5',
          border: '1px solid #E8ECF0',
          '& .MuiToggleButtonGroup-grouped': {border: 'none !important'}
        }}
      >
        <ToggleButton value={true} sx={{border: 'none'}} data-test-id="clokwise-btn">
          <RotateRightIcon />
          <Typography>{t('kilnConfig.kilnClockwiseRotation')}</Typography>
        </ToggleButton>
        <ToggleButton value={false} sx={{border: 'none'}} data-test-id="counter-clokwise-btn">
          <RotateLeftIcon />
          <Typography>{t('kilnConfig.kilnCounterClockwiseRotation')}</Typography>
        </ToggleButton>
      </ToggleButtonGroup>
    </Stack>
  )
}
